import React, { useState, useLayoutEffect } from 'react';
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem';
import { FormWrapper } from './Wrappers';
import { genericMemo } from './Types';

export function isValid<Value extends string | number, Option>({value, options, valueGetter}: {value: Value, options: Option[], valueGetter: (option: Option) => Value}): boolean {
  return options.find(option => valueGetter(option) === value) !== undefined
}

enum New { NEW = 'NEW' }

function SelectInputRaw<Value extends string | number, Option, CreationArgs>(
  {options, valueGetter, nameGetter, value, onChange, onCreate, required, CreationDialog, autoSelectSingle = true, ...props}:
  {
    options: Option[],
    valueGetter: (option: Option) => Value,
    nameGetter: (option: Option) => string,
    value: Value | null,
    onChange: (newValue: Value) => void,
    onCreate?: ((args: CreationArgs) => void),
    required: boolean,
    CreationDialog?: (({onCreate, onCancel}: {onCreate: (args: CreationArgs) => void, onCancel: () => void}) => React.JSX.Element) | null,
    // When there's a single option, pre-select it (default). Turn OFF for action pickers where
    // onChange has a side effect (e.g. "add" menus) — otherwise the sole option auto-fires it.
    autoSelectSingle?: boolean,
    label?: string,
    disabled?: boolean,
    sx?: {},
    // Forwarded to the underlying MUI Select — e.g. to lazily load options the first time the
    // menu opens. Flows through `...props` → FormWrapper extraProps → the Select.
    onOpen?: () => void,
  }) {
  const [dialogEnabled, setDialogEnabled] = useState(false)

  useLayoutEffect(() => {
    // Pre-fill when there's exactly one option, but only if nothing is chosen yet.
    // Guarding on `value === null` (rather than "differs from the sole option")
    // means an existing selection — even a stale one no longer in `options` — is
    // left untouched, so it surfaces as a blank/required-error for the user to
    // re-pick instead of being silently overwritten.
    if (autoSelectSingle && options.length === 1 && value === null) {
      onChange(valueGetter(options[0]))
    }
  }, [options, valueGetter, value, autoSelectSingle])

  const creationEnabled = CreationDialog !== undefined && CreationDialog !== null
  const selectedOptionOrUndefined = options.find(option => valueGetter(option) === value)
  const selectedOption = selectedOptionOrUndefined === undefined || value === null ? '' : value

  function handleChange(newValue: Value | New) {
    if (newValue === New.NEW) {
      setDialogEnabled(true)
    } else {
      onChange(newValue)
    }
  }

  function handleCreate(args: CreationArgs) {
    if (onCreate !== undefined) {
      onCreate(args)
    }
    setDialogEnabled(false)
  }

  function handleCancel() {
    setDialogEnabled(false)
  }

  const TheSelect = FormWrapper(
    {
      Component:
        ({options, onChange, ...props}: {options: Option[], onChange: (newValue: Value) => void}) =>
          <Select {...props} onChange={(e) => {onChange(e.target.value as Value)}}>
            {creationEnabled && <MenuItem value={New.NEW}><strong>Ny…</strong></MenuItem>}
            {options.map(option => <MenuItem key={valueGetter(option)} value={valueGetter(option)}>{nameGetter(option)}</MenuItem>)}
          </Select>,
      validationFunction: (value: Value | "") => !required || isValid({value, options, valueGetter})
    }
  )

  return (
    <>
      <TheSelect
        {...props}
        value={selectedOption}
        options={options}
        onChange={handleChange}
      />
      {dialogEnabled && creationEnabled && <CreationDialog onCreate={handleCreate} onCancel={handleCancel}/>}
    </>
  )

}

const SelectInput = genericMemo(SelectInputRaw)
export default SelectInput
